import secure_compare from "secure-compare";
import { getLineAccessToken } from "../../../../services/auth/auth.service";
import { getChannelId, parseLineArg } from "../utils/lineIds";

function decodeJwt(token: string) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payloadJson = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson);
    return {
      payload
    };
  } catch (error) {
    console.error("JWT Decode error:", error);
    return null;
  }
}


class LineLogin {

  api_version = "v2.1";
  channel_id: string;
  callback_url: string;
  scope: string;
  endpoint = "line.me";
  prompt: string;
  bot_prompt: string;
  verify_id_token: boolean;
  defaultLoginMethod?: string;

  constructor(defaultLoginMethod?: string) {
    this.defaultLoginMethod = defaultLoginMethod;
    const lineArg = parseLineArg(defaultLoginMethod);
    this.channel_id = getChannelId(lineArg);
    this.callback_url = `${process.env.REACT_APP_PUBLIC_BASE_URL}/login` || "";
    this.scope = process.env.REACT_APP_PUBLIC_LINE_LOGIN_SCOPE || "";
    this.prompt = process.env.REACT_APP_PUBLIC_LINE_LOGIN_PROMPT || "";
    this.bot_prompt = process.env.REACT_APP_PUBLIC_LINE_LOGIN_BOT_PROMPT || "";
    this.verify_id_token = true;
  }


  auth(promptOverride?: string): string {
    let state = LineLogin._random();
    let nonce = LineLogin._random();
    let url = this.make_auth_url(state, nonce, promptOverride);
    localStorage.setItem("nonce", nonce);
    localStorage.setItem("state", state);

    return url;
  }


  make_auth_url(state: any, nonce: any, overridePrompt?: string, overrideBotPrompt?: string): string {
    const client_id = encodeURIComponent(this.channel_id);
    const redirect_uri = encodeURIComponent(this.callback_url);
    const scope = encodeURIComponent(this.scope);
    const prompt = encodeURIComponent(this.prompt);
    const bot_prompt = encodeURIComponent(this.bot_prompt);
    let url = `https://access.${this.endpoint}/oauth2/${this.api_version}/authorize?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&scope=${scope}&bot_prompt=${bot_prompt}&state=${state}`;
    // if (prompt) url += `&prompt=${encodeURIComponent(this.prompt)}`;
    if (overridePrompt) url += `&prompt=${encodeURIComponent(overridePrompt)}`;
    if (nonce) url += `&nonce=${encodeURIComponent(nonce)}`;
    return url;
  }

  async callback(code: string, state: string): Promise<any> {

    let nonce: any = localStorage.getItem("nonce") || "";
    let state_session = localStorage.getItem("state") || "";

    if (!code || !state) {
      throw new Error("code or state is null");
    }
    if (!secure_compare(state, state_session)) {
      throw new Error("invalid state");
    }

    const validateIsFriend = async (accessToken: string) => {
      try {
        const url = "https://api.line.me/friendship/v1/status";

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          }
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Error: ${response.status} - ${errorData.message || "Unknown error"}`);
        }

        const result = await response.json();
        return result?.friendFlag || false;
      } catch (error: any) {
        console.error("Error checking friendship status:", error);
        throw error;
      }
    };


    try {
      if (this.defaultLoginMethod === undefined) return;
      const res: any = await getLineAccessToken(code, this.defaultLoginMethod);
      if (this.verify_id_token && res.id_token) {
        const access_token = res.access_token;

        const decode_id_token = decodeJwt(res.id_token);
        if (!decode_id_token) {
          throw new Error("invalid id_token");
        }
        if (decode_id_token.payload.nonce && !secure_compare(decode_id_token.payload.nonce, nonce)) {
          throw new Error("invalid nonce");
        }

        localStorage.setItem("access_token", access_token);

        localStorage.setItem("id_token", res.id_token);

        const isFriend = await validateIsFriend(access_token);

        if (!isFriend) {
          const isRelogin = localStorage.getItem("relogin");
          if (!isRelogin) {
            localStorage.setItem("relogin", "true");
            const state = LineLogin._random();
            const nonce = LineLogin._random();
            localStorage.setItem("state", state);
            localStorage.setItem("nonce", nonce);

            // const redirectUrl = this.auth("consent");
            const client_id = encodeURIComponent(this.channel_id);
            const redirect_uri = encodeURIComponent(this.callback_url);
            const scope = encodeURIComponent(this.scope);
            const bot_prompt = "aggressive";
            const prompt = "consent";
            // const redirectUrl = `https://access.line.me/oauth2/v2.1/authorize/consent?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&scope=${scope}&state=${state}&bot_prompt=${bot_prompt}&prompt=${prompt}&nonce=${nonce}`;
            const redirectUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&scope=${scope}&state=${state}&bot_prompt=${bot_prompt}&prompt=${prompt}&nonce=${nonce}`;
            // window.location.href = redirectUrl;

            return {
              actionRequired: "add_friend",
              redirectUrl: redirectUrl
            }
          } else {
            localStorage.removeItem("relogin");
            ["nonce", "state"].forEach(key => localStorage.removeItem(key));
            throw new Error("User must add the bot as a friend to continue.");
          }

        }
        ["nonce", "state"].forEach(key => localStorage.removeItem(key));

        return {
          actionRequired: "success",
          payload: decode_id_token.payload
        };
      }
      throw new Error("Missing id_token from LINE response");
    } catch (error: any) {
      console.log("error", error.message);
      throw error;
    }
  }

  static _random() {
    const array = new Uint8Array(20);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, "0")).join("");
  }

}

export default LineLogin;
